import type { AgencyResponse } from '@saaga/shared/interfaces/agency.interface'
import type { AgentEdge, AgentNode } from '@saaga/shared/interfaces/agency-node.interface'
import { useCallback, useState } from 'react'
import type { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import type { AgencyFormSchemaType } from '@/components/agencies/agency-form.schema'
import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { agencyRoutes, orgRoutes } from '@/routes/org-routes.const'

import { serializeFlowData } from './agency-flow-serialize.util'
import { useAgencyFormInit } from './use-agency-form-init.hook'
import { useAgencyLogo } from './use-agency-logo.hook'
import { useAgencySaveDraft } from './use-agency-save-draft.hook'

interface UseAgencyFormProps {
  agencyResponse?: AgencyResponse | null
  isEditMode?: boolean
}

export interface UseAgencyFormReturn {
  form: ReturnType<typeof useForm<AgencyFormSchemaType>>
  isLoading: boolean
  isFlowDirty: boolean
  logoPreviewUrl: string | null
  visibility: AgencyFormSchemaType['visibility']
  version: number
  flowDataRef: React.MutableRefObject<{ nodeList: AgentNode[]; edgeList: AgentEdge[] }>
  handleBack: () => void
  handleLogoChange: (file: File | null) => void
  handleLogoRemove: () => void
  handleSaveDraft: () => Promise<void>
  handlePublishToOrganization: () => Promise<void>
  handleSubmitForReview: () => Promise<void>
  handleFlowChange: (nodeList: AgentNode[], edgeList: AgentEdge[]) => void
}

export const useAgencyForm = ({
  agencyResponse = null,
  isEditMode = false,
}: UseAgencyFormProps): UseAgencyFormReturn => {
  const navigate = useNavigate()
  const orgSlug = useOrgSlug()
  const [isFlowDirty, setIsFlowDirty] = useState(false)

  const { form, flowDataRef, initialFlowSnapshotRef } = useAgencyFormInit(agencyResponse)

  const handleFlowChange = useCallback(
    (nodeList: AgentNode[], edgeList: AgentEdge[]): void => {
      flowDataRef.current = { nodeList, edgeList }
      const currentSnapshot = serializeFlowData({ nodeList, edgeList })
      setIsFlowDirty(currentSnapshot !== initialFlowSnapshotRef.current)
    },
    [flowDataRef, initialFlowSnapshotRef]
  )

  const handleBack = useCallback((): void => {
    navigate(`/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.studio}`)
  }, [navigate, orgSlug])

  const logoUrl = agencyResponse?.presentation_image_path ?? null
  const logoState = useAgencyLogo({ logoUrl, form })
  const { isLoading, handleSaveDraft, handlePublishToOrganization, handleSubmitForReview } =
    useAgencySaveDraft({
      form,
      flowDataRef,
      isEditMode,
      existingSlug: agencyResponse?.slug,
    })

  return {
    form,
    isLoading,
    isFlowDirty,
    logoPreviewUrl: logoState.logoPreviewUrl,
    visibility: form.watch('visibility'),
    version: form.watch('version'),
    flowDataRef,
    handleBack,
    handleLogoChange: logoState.handleLogoChange,
    handleLogoRemove: logoState.handleLogoRemove,
    handleSaveDraft,
    handlePublishToOrganization,
    handleSubmitForReview,
    handleFlowChange,
  }
}
